import React, { useContext, useEffect, useState } from 'react'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import Box from '@mui/material/Box'
import useMediaQuery from '@mui/material/useMediaQuery'
import Typography from '@mui/material/Typography'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import BusinessIcon from '@mui/icons-material/Business'
import Circle from '@mui/icons-material/Circle'
import NewExpenseForm from './NewExpenseForm'
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'
import AddIcon from '@mui/icons-material/Add'
import { ExpenseContext } from '../context/ExpenseContext'
import AdxButton from 'components/custom/AdxButton'

const NewExpenseModal = ({ fromActionButton = false, reportData, previousSelectedCategory }) => {
  const isLarge = useMediaQuery((theme) => theme.breakpoints.up('lg'))

  // new expense modal states and function
  const { savedExpenseSession } = useContext(ExpenseContext)
  const [isModalOpen, setModalOpen] = useState(fromActionButton)
  const handleOpen = () => {
    setModalOpen(true)
  }
  const handleClose = () => {
    if (selectedCategory) {
      handleExitOpen()
    } else {
      setModalOpen(false)
      setSelectedCategory('')
    }
  }
  const [selectedCategory, setSelectedCategory] = useState(previousSelectedCategory)

  // success info modal states and function
  const [isInfoModalOpen, setInfoModalOpen] = useState(false)
  useEffect(() => {
    // browser prompt on reload
    if (isModalOpen) {
      window.onbeforeunload = function (e) {
        e.preventDefault()
        e.returnValue = true
      }
    }
    return () => {
      window.onbeforeunload = null
    }
  }, [isModalOpen])

  const handleInfoClose = () => {
    setInfoModalOpen(false)
    setSelectedCategory('')
  }

  // exit modal states and function
  const [isExitModalOpen, setExitModalOpen] = useState(false)
  const handleExitOpen = () => {
    setExitModalOpen(true)
  }
  const handleExitClose = () => {
    setExitModalOpen(false)
  }
  const handleDeleteExit = () => {
    setSelectedCategory('')
    delete savedExpenseSession.resumeStatus
    setModalOpen(false)
    setExitModalOpen(false)
  }

  return (
    <Box>
      <AdxButton
        variant={'outlined'}
        onClick={handleOpen}
        sx={{
          display: 'flex',
          gap: 1,
        }}
      >
        <AddIcon />
        <Typography variant="body1" fontWeight="bold">
          {isLarge ? 'Add expense' : 'Add'}
        </Typography>
      </AdxButton>
      <AdxFormModal
        open={isModalOpen}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick') {
            handleClose()
          }
        }}
        handleClose={handleClose}
        icon={
          reportData.claim_report_type === 'Personal' ? (
            <AccountCircleIcon sx={{ fontSize: 43 }} />
          ) : (
            <BusinessIcon sx={{ fontSize: 25 }} />
          )
        }
        title={reportData.title}
        subtitle={
          <Box
            display="flex"
            alignItems={{ xs: 'start', lg: 'center' }}
            columnGap={1}
            flexDirection={{ xs: 'column', lg: 'row' }}
          >
            <Typography component="span" variant="body1" color="alternate.main" fontWeight="bold">
              {reportData.claim_report_type}
            </Typography>
            <Circle
              color="light-gray-text"
              sx={{ fontSize: 7, display: { xs: 'none', md: 'block' } }}
            />
            <Typography color="light-gray-text.main">
              {`${reportData.start_date} - ${reportData.end_date}`}
            </Typography>
          </Box>
        }
        disableAutoFocus
        sxModal={{
          width: { xs: 'calc(100vw - 24px)', sm: 'calc(100vw - 24px)', lg: 700 },
          minWidth: { xs: 'calc(100vw - 24px)', sm: 'auto' },
          maxWidth: { xs: 'calc(100vw - 24px)', sm: 'calc(100vw - 24px)', lg: 700 },
          maxHeight: { xs: 'calc(100dvh - 24px)', sm: 'calc(100dvh - 24px)' },
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          borderRadius: '10px',
        }}
      >
        <NewExpenseForm
          reportData={reportData}
          reportId={reportData.id}
          handleClose={handleClose}
          setModalOpen={setModalOpen}
          setInfoModalOpen={setInfoModalOpen}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
        />
      </AdxFormModal>

      {/* modal on success */}
      <AdxInfoModal
        open={isInfoModalOpen}
        onClose={handleInfoClose}
        handleClose={handleInfoClose}
        handleAction={() => alert('Action button pressed')}
        muiIcon="check_circle"
        iconColor="alternate.main"
        title="Expense successfully added"
        subtitle="This expense has been added to your report. Your overall claim amount has been updated."
        footerButtons
        firstBtnLabel="Add another expense"
        firstBtnHandle={() => {
          setSelectedCategory('')
          handleInfoClose()
          handleOpen()
        }}
        secondBtnLabel="Return to claim report"
        secondBtnHandle={handleInfoClose}
      ></AdxInfoModal>

      {/* modal on exit */}
      <AdxInfoModal
        variant={'error'}
        open={isExitModalOpen}
        disableAutoFocus
        onClose={(_, reason) => {
          if (reason !== 'backdropClick') {
            handleExitClose()
          }
        }}
        handleClose={handleExitClose}
        muiIcon="error"
        iconColor="error.main"
        title="Exit?"
        subtitle="Are you sure you want to leave this page?"
        footerButtons
        firstBtnLabel="Exit"
        firstBtnHandle={handleDeleteExit}
        secondBtnLabel="Keep editing"
        secondBtnHandle={handleExitClose}
      ></AdxInfoModal>
    </Box>
  )
}

export default NewExpenseModal
